import { useState, useEffect } from 'react';
import { getNextTheme } from '../constants/subThemes';

/**
 * Reusable hook for managing admin section state and data fetching
 * Consolidates the common pattern used across Users, Products, and AI sections
 *
 * @param {Object} config - Configuration object
 * @param {string} config.tabName - The tab name to match activeTab
 * @param {Function} config.apiOverview - API function to fetch overview data
 * @param {Function} config.apiDetail - API function to fetch detail data
 * @param {Function} config.getInitialSelectedId - UIState getter for initial selected ID
 * @param {Function} config.setPersistedSelectedId - UIState setter to persist selected ID
 * @param {Function} config.extractList - Function to extract list from overview data (e.g., data => data.users)
 * @param {Function} config.transformListItem - Optional function to transform each list item (e.g., snake_case to camelCase)
 * @param {Function} config.transformDetailItem - Optional function to transform detail item
 * @param {string} config.listKey - Key to identify items in the list (default: 'id')
 * @param {string} config.activeTab - Current active tab
 *
 * @returns {Object} - State and handlers for the admin section
 */
export default function useAdminSection({
  tabName,
  apiOverview,
  apiDetail,
  getInitialSelectedId,
  setPersistedSelectedId,
  extractList,
  transformListItem,
  transformDetailItem,
  listKey = 'id',
  activeTab
}) {
  // State
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(() => getInitialSelectedId?.() || null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [overviewData, setOverviewData] = useState(null);

  // Refresh overview/list data
  const refreshOverview = async () => {
    try {
      setIsLoadingList(true);
      const data = await apiOverview();
      setOverviewData(data);

      let list = extractList ? extractList(data) : data;
      // Transform list items if transformer provided
      if (transformListItem && list) {
        list = list.map(transformListItem);
      }

      // Attach theme to each item based on display order
      // If items have is_active property, split into active/inactive and assign themes accordingly
      if (list && list.length > 0) {
        const hasActiveProperty = list[0] && ('is_active' in list[0]);

        if (hasActiveProperty) {
          // Split by active status (matches sidebar display order)
          const activeItems = list.filter(item => item.is_active);
          const inactiveItems = list.filter(item => !item.is_active);

          list = [
            ...activeItems.map((item, idx) => ({ ...item, theme: getNextTheme(idx) })),
            ...inactiveItems.map((item, idx) => ({ ...item, theme: getNextTheme(activeItems.length + idx) }))
          ];
        } else {
          // Just assign themes sequentially
          list = list.map((item, idx) => ({ ...item, theme: getNextTheme(idx) }));
        }
      }

      setItems(list || []);

      // AUTOMATIC SELECTION VALIDATION
      // After refreshing the list, check if the currently selected item still exists.
      // If not (e.g., it was deleted), automatically clear the selection.
      // This prevents race conditions and 404 errors when items are deleted.
      // Components should NOT manually clear selection before calling refreshOverview.
      if (selectedId && list) {
        if (list.length === 0) {
          // List is empty, clear selection immediately
          handleSetSelectedId(null);
        } else {
          const itemExists = list.some(item => item[listKey] === selectedId);
          if (!itemExists) {
            // Item was deleted, clear selection immediately
            handleSetSelectedId(null);
          }
        }
      }
    } catch (error) {
      console.error(`Failed to fetch ${tabName} overview:`, error);
      setOverviewData(null);
      setItems([]);
    } finally {
      setIsLoadingList(false);
    }
  };

  // Refresh detail data
  const refreshDetail = async () => {
    if (!selectedId) {
      setSelectedItem(null);
      return;
    }

    // Check if the selected item exists in the current items list
    // This prevents fetching deleted items or non-existent items
    if (items.length === 0) {
      // No items at all, clear selection and detail
      setSelectedItem(null);
      handleSetSelectedId(null);
      return;
    }

    const itemExists = items.some(item => item[listKey] === selectedId);
    if (!itemExists) {
      setSelectedItem(null);
      handleSetSelectedId(null);
      return;
    }

    try {
      setIsLoadingDetail(true);
      let item = await apiDetail(selectedId);
      // Transform detail item if transformer provided
      if (transformDetailItem && item) {
        item = transformDetailItem(item);
      }

      // Find theme from the list item and attach it to detail
      const listItem = items.find(i => i[listKey] === selectedId);
      if (listItem?.theme) {
        item.theme = listItem.theme;
      }

      setSelectedItem(item);
    } catch (error) {
      // If item not found (404), silently clear the selection
      if (error.status === 404) {
        setSelectedItem(null);
        handleSetSelectedId(null);
      } else {
        console.error(`Failed to fetch ${tabName} detail:`, error);
        setSelectedItem(null);
      }
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // Handle selection change with persistence
  const handleSetSelectedId = (id) => {
    setSelectedId(id);
    if (setPersistedSelectedId) {
      setPersistedSelectedId(id);
    }
  };

  // Fetch overview when switching to this tab
  useEffect(() => {
    if (activeTab === tabName) {
      refreshOverview();
    }
  }, [activeTab]);

  // Fetch detail when selectedId changes (only if on this tab)
  useEffect(() => {
    if (activeTab !== tabName) {
      return;
    }

    if (!selectedId) {
      // Clear detail when selection is cleared
      setSelectedItem(null);
      return;
    }

    // Check if selected item exists in current items list before fetching
    if (items.length === 0) {
      setSelectedItem(null);
      handleSetSelectedId(null);
      return;
    }

    const itemExists = items.some(item => item[listKey] === selectedId);
    if (!itemExists) {
      setSelectedItem(null);
      handleSetSelectedId(null);
      return;
    }

    // Only fetch if item exists in list
    refreshDetail();
  }, [selectedId, activeTab, items]);

  return {
    // State
    items,
    selectedId,
    selectedItem,
    isLoadingList,
    isLoadingDetail,
    overviewData,

    // Handlers
    setSelectedId: handleSetSelectedId,
    refreshOverview,
    refreshDetail,
    refreshBoth: async () => {
      await Promise.all([refreshOverview(), refreshDetail()]);
    }
  };
}
